/**
 * VRG-491 — the ONE place a `scans.invalid_reason` becomes a severity, a colour and a
 * sentence. Both surfaces that show the invalid-scan indicator read this module: the
 * certificates table cell (`components/display/Table/.../Cells.tsx`) and the certificate
 * page header (`pages/main/CertificatePage`). Neither restates the mapping.
 *
 * ## Why the mapping lives on the CLIENT and not in SQL
 *
 * The API deliberately sends raw per-reason counts (`invalid_reason_counts`) and no
 * severity. Severity is a presentation decision — it picks a colour and a tier — and
 * putting it server-side would mean the reason list existed in two places the moment a
 * third surface wanted a different cut of it. The API reports facts; this decides what
 * they look like.
 *
 * ## Tamper is NOT here, and that is the governing distinction
 *
 * `scans.tampered` is a STATE — durable, binary, one per certificate — so it renders as a
 * badge beside the status (`TamperBadge`). An invalid scan is an EVENT, and a certificate
 * with three of them is not itself invalid; three taps failed, possibly by someone with no
 * connection to the owner. So invalid renders as an icon plus a tally, never as a badge.
 */

/**
 * Every value `scans.invalid_reason` is allowed to hold, per the `scans_invalid_reason_check`
 * constraint in `apps/api/src/database/schema/tables.ts`.
 *
 * Mirrors `InvalidReason` in `apps/api/src/domain/repositories/scan.repository.ts`. The copy
 * in `hooks/api/useCertificateScanRecords.ts` had drifted — it was missing `tag_replaced`
 * (VRG-268) and `tag_held` (VRG-275) — and now re-exports this one so there is a single
 * union on this side of the wire.
 */
export type InvalidReason =
  | 'invalid_global_id'
  | 'invalid_proof_format'
  | 'invalid_key'
  | 'invalid_proof'
  | 'duplicate_proof'
  | 'stale_counter'
  | 'tag_replaced'
  | 'tag_held'

/**
 * The key the API buckets a reasonless invalid scan under. Not a database value — the
 * `invalidConsistencyCheck` constraint forbids `invalid = true` with a NULL reason today —
 * but rows predating that constraint exist (18 locally), and dropping them would make the
 * breakdown disagree with the count it is a breakdown of.
 */
export const UNCLASSIFIED_REASON = 'unclassified'

/**
 * Two tiers, because the indicator has two colours.
 *
 * `spoof` earns red: someone presented something we can prove we did not issue, or replayed
 * a proof. `benign` is amber, which is the honest epistemic status of "worth a look" — an
 * old URL out of somebody's history, a tag we ourselves retired, a mangled link.
 *
 * Red is not the default for anything merely suspicious. Red is reserved in this app for
 * destructive and error states (working-conventions §32); spend it on ambiguity and people
 * stop reading it, which degrades the signal everywhere it does mean act-now.
 */
export type InvalidScanSeverity = 'spoof' | 'benign'

/**
 * The reasons that mean someone presented a tag we can prove we never issued, or replayed a
 * proof that was already used. Everything NOT in this set is benign — including a reason
 * this bundle has never heard of, which is the safe default: an unrecognised token is not
 * evidence of an attack, and colouring it red would let a future API release repaint the
 * table without anyone deciding to.
 *
 * ⚠️ `invalid_key` is deliberately NOT in this set, and this is a considered DEPARTURE from
 * the mapping as first written (VRG-491's ticket body lists it under spoof, on the reading
 * that "crypto failed"). The code says otherwise:
 * `apps/api/src/domain/services/nfc-verification.service.ts:205-225` emits `invalid_key` in
 * exactly two situations, and both are OUR fault, not the scanner's — the tag record carries
 * no `secretName`/`secretVersion`, or Secret Manager returned nothing for the one it does
 * carry. It is a Vera-side key-material failure and is never evidence of a spoof.
 *
 * The failure mode is not hypothetical and has happened twice. `parse-secret-manager-
 * credentials.ts` records an outage (`invalid_rapt`) that "turned EVERY scan into
 * `invalid_key`", and `gcp-secret-material.repository.ts` records a second (VERA-790,
 * read/write resolving different projects). Under the spoof mapping, a credentials outage
 * paints every certificate in the table red and accuses the brand's customers of forging
 * tags — the exact alarm-fatigue collapse the amber tier exists to prevent, triggered by an
 * operational incident rather than by any attack. Amber says "worth a look", which is true
 * and actionable; red would be a false accusation the product can already prove is false.
 *
 * `docs/guides/scan-session-verification.md` (row T8) reaches the same conclusion for the
 * live-scan surfaces: showing `invalid_key` "sends an operator to inspect a tag that is fine".
 */
const SPOOF_REASON_LIST: readonly InvalidReason[] = [
  // The proof did not verify against the key we issued for this tag.
  'invalid_proof',
  // A global id we have no record of ever issuing.
  'invalid_global_id',
  // A proof that already verified once, presented again. A clone replaying a captured
  // proof looks exactly like a double-tap of a real tag, so this tier is the least certain
  // of the three and its base rate is worth watching before it is trusted.
  'duplicate_proof',
]

const SPOOF: ReadonlySet<string> = new Set<string>(SPOOF_REASON_LIST)

/**
 * Short, plain-language wording for each reason. Never the raw enum token.
 *
 * `docs/guides/scan-session-verification.md` (T8) makes this a rule rather than a
 * preference: the raw `invalidReason` is a diagnostic and must not reach a user-facing
 * surface. `invalid_key` is the case that shows why — the token names a key, which reads as
 * an accusation against the tag, when what actually happened is that our own key store did
 * not answer. Every string below says what a reviewer would need to know, in their terms.
 */
export const INVALID_REASON_LABEL: Record<string, string> = {
  invalid_proof: "Proof didn't verify",
  invalid_global_id: 'Tag id not recognised',
  duplicate_proof: 'Proof reused',
  // Ours, not theirs — the wording must not send a reviewer to inspect good hardware.
  invalid_key: 'Key unavailable on our side',
  invalid_proof_format: 'Malformed link',
  stale_counter: 'Old link reused',
  tag_replaced: 'Retired tag',
  tag_held: 'Tag not released yet',
  [UNCLASSIFIED_REASON]: 'Reason not recorded',
}

/** The severity of a single reason. A missing, null or unrecognised reason is benign. */
export const invalidReasonSeverity = (
  reason: string | null | undefined,
): InvalidScanSeverity => (reason && SPOOF.has(reason) ? 'spoof' : 'benign')

/** Human wording for a reason, falling back to the unclassified string. */
export const invalidReasonLabel = (reason: string | null | undefined): string =>
  INVALID_REASON_LABEL[reason ?? UNCLASSIFIED_REASON] ??
  INVALID_REASON_LABEL[UNCLASSIFIED_REASON]

export type InvalidScanSummary = {
  /** How many scans failed validation, in total. Always > 0 when a summary exists. */
  count: number
  /** The WORST severity ever seen on this certificate. See the note on aggregation below. */
  severity: InvalidScanSeverity
  /** The most common reason within the severity tier that decided the colour. */
  topReason: string
  /** `topReason`'s user-facing wording. */
  topReasonLabel: string
}

/**
 * Roll a certificate's per-reason counts up into the one thing the indicator renders.
 *
 * ## Aggregation is by MAX severity, never by most-recent
 *
 * If a genuine spoof happened last month and three stale replays have landed since, a
 * most-recent rule renders amber and HIDES the spoof. A later benign scan is not evidence
 * the earlier attack did not happen, so the worst thing ever seen stays surfaced until a
 * person reviews it. ("Is this being attacked right now" is a different question and would
 * be a recent-window signal ADDED beside this one, never a replacement for it.)
 *
 * ## The top reason comes from the tier that set the colour
 *
 * Naming the globally most-frequent reason would let the tooltip contradict the glyph — one
 * spoof among thirty stale replays reads red while the sentence says "old link reused",
 * which tells the reviewer the opposite of what the colour is warning about. So the tally is
 * taken within the winning tier only.
 *
 * Ties break on the authored `SPOOF_REASON_LIST` order first, then alphabetically, so the
 * same data always produces the same sentence rather than flickering between renders.
 *
 * @param counts `invalid_reason_counts` from the API: `{reason: count}`, `unclassified` for
 *   rows carrying no reason. Tolerates undefined for a payload predating this field.
 * @param total `invalid_scan_count`. Used when `counts` is missing or empty so an older API
 *   response still renders a count rather than silently dropping the indicator.
 */
export const summarizeInvalidScans = (
  counts: Record<string, number> | null | undefined,
  total: number | null | undefined,
): InvalidScanSummary | null => {
  const entries = Object.entries(counts ?? {}).filter(([, n]) => Number(n) > 0)
  const summed = entries.reduce((acc, [, n]) => acc + Number(n), 0)
  // Prefer the explicit total: it is the same subquery the boolean flag derives from, so a
  // breakdown that somehow disagreed would still not undercount the tally the user reads.
  const count = Number(total) > 0 ? Number(total) : summed
  if (!count) return null

  // No breakdown at all (older payload, or every row unclassified and dropped upstream).
  // Render the count, take the safe tier, and say plainly that we do not know why.
  if (!entries.length) {
    return {
      count,
      severity: 'benign',
      topReason: UNCLASSIFIED_REASON,
      topReasonLabel: invalidReasonLabel(UNCLASSIFIED_REASON),
    }
  }

  const severity: InvalidScanSeverity = entries.some(
    ([reason]) => invalidReasonSeverity(reason) === 'spoof',
  )
    ? 'spoof'
    : 'benign'

  const tier = entries.filter(
    ([reason]) => invalidReasonSeverity(reason) === severity,
  )
  const rank = (reason: string) => {
    const i = SPOOF_REASON_LIST.indexOf(reason as InvalidReason)
    return i === -1 ? SPOOF_REASON_LIST.length : i
  }
  tier.sort(
    ([aReason, aN], [bReason, bN]) =>
      Number(bN) - Number(aN) ||
      rank(aReason) - rank(bReason) ||
      aReason.localeCompare(bReason),
  )
  const topReason = tier[0][0]

  return {
    count,
    severity,
    topReason,
    topReasonLabel: invalidReasonLabel(topReason),
  }
}
